import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import { NavBar } from 'components/NavBar/NavBar'
import { FilterEnum } from 'components/Filter/SingleFilter'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearInventoryIssues, getInventoryIssues } from 'features/inventory/inventoryIssuesSlice'
import {
  clearInventoryAssignments,
  getInventoryAssignments,
} from 'features/inventory/inventoryItemsSlice'
import {
  clearInventoryCustody,
  clearInventoryMovements,
  getInventoryCustody,
  getInventoryMovements,
} from 'features/inventory/inventoryControlSlice'
import { Flex, MainHeadline, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { getParams } from 'utils/helpers'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import {
  IStoreCounterConfirmResult,
  IStoreCounterTab,
  IStoreCustodyRow,
  IStoreLedgerRow,
  IStoreReturnFocus,
} from 'types/inventoryCounter.interface'
import { inventoryAssignmentStatuses } from '../Assignments/InventoryAssignments'
import { ServeTab } from './ServeTab'
import { ReturnTab } from './ReturnTab'
import { OutstandingTab } from './OutstandingTab'
import { HistoryTab } from './HistoryTab'

const COUNTER_HEADER_SUBLINE =
  'One counter for the queue — serve consumables and tools, take returns, track custody.'

const COUNTER_TABS: IStoreCounterTab[] = [
  { id: 'serve', label: 'Serve' },
  { id: 'return', label: 'Return' },
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'history', label: 'History' },
]

const initialTab = (pathname: string, state: unknown): IStoreCounterTab['id'] => {
  if ((state as { counterTab?: IStoreCounterTab['id'] } | null)?.counterTab === 'return') return 'return'
  if (pathname.includes('/inventory/assignments')) return 'outstanding'
  if (pathname.includes('/inventory/issues')) return 'history'
  return 'serve'
}

export const StoreCounter = () => {
  const routerLocation = useLocation()
  const [tab, setTab] = useState<IStoreCounterTab['id']>(
    initialTab(routerLocation.pathname, routerLocation.state),
  )
  const [params, setParams] = useState<any>(getParams({ location }))
  const [localLedger, setLocalLedger] = useState<IStoreLedgerRow[]>([])
  const [localCustody, setLocalCustody] = useState<IStoreCustodyRow[]>([])
  const [returnedCustodyIds, setReturnedCustodyIds] = useState<string[]>([])
  const [returnFocus, setReturnFocus] = useState<IStoreReturnFocus | null>(null)

  useQueryPagination({
    fetchFunctions: [
      getInventoryIssues,
      getInventoryAssignments,
      getInventoryCustody,
      getInventoryMovements,
    ],
    cleanUpFunctions: [
      clearInventoryIssues,
      clearInventoryAssignments,
      clearInventoryCustody,
      clearInventoryMovements,
    ],
    params,
  })

  const handleDemoConfirm = (result: IStoreCounterConfirmResult) => {
    if (result.ledgerRows.length) setLocalLedger((prev) => [...result.ledgerRows, ...prev])
    if (result.custodyRows.length) setLocalCustody((prev) => [...result.custodyRows, ...prev])
  }

  const mergedCustody = useMemo<IStoreCustodyRow[]>(() => {
    const returnedLabel = `Returned ${format(new Date(), 'd MMM')}`
    return localCustody.map((row) =>
      returnedCustodyIds.includes(row.id)
        ? { ...row, status: 'RETURNED', pillLabel: 'Returned', dueLabel: returnedLabel }
        : row,
    )
  }, [localCustody, returnedCustodyIds])

  const handleDemoReturn = (custodyIds: string[], ledgerRows: IStoreLedgerRow[]) => {
    if (custodyIds.length) setReturnedCustodyIds((prev) => [...prev, ...custodyIds])
    if (ledgerRows.length) setLocalLedger((prev) => [...ledgerRows, ...prev])
  }

  const handleProcessReturn = (row: IStoreCustodyRow) => {
    setReturnFocus({ holder: row.holder, custodyId: row.id })
    setTab('return')
  }

  const handleProcessReturnAssignment = (assignment: IGetAllInventoryAssignmentsSingleResponse) => {
    setReturnFocus({
      holder: assignment.assignee
        ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}`
        : '—',
      custodyId: `ASG-${assignment.id}`,
      assignmentId: assignment.id,
    })
    setTab('return')
  }

  const filters = [
    {
      type: FilterEnum.SELECT,
      name: 'Status',
      options: inventoryAssignmentStatuses,
      propName: 'status',
    },
  ]

  return (
    <>
      {tab === 'outstanding' ? (
        <NavBar
          enableTemporaryManualClosing
          filterOptions={filters}
          visibleFilterOverflow
          setParams={setParams}
        />
      ) : (
        <NavBar />
      )}
      <PageWrapper>
        <Flex between center mb='16'>
          <Flex column gap='0.25rem'>
            <MainHeadline>Store Counter</MainHeadline>
            <OtherText grey>{COUNTER_HEADER_SUBLINE}</OtherText>
          </Flex>
        </Flex>
        <Flex mb='16'>
          <TabsContainer>
            {COUNTER_TABS.map((t) => (
              <Tabs key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                {t.label}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        {tab === 'serve' && <ServeTab onDemoConfirm={handleDemoConfirm} />}
        {tab === 'return' && (
          <ReturnTab
            custodyRows={mergedCustody}
            onDemoReturn={handleDemoReturn}
            returnFocus={returnFocus}
            onReturnFocusHandled={() => setReturnFocus(null)}
            localLedger={localLedger}
          />
        )}
        {tab === 'outstanding' && (
          <OutstandingTab
            params={params}
            setParams={setParams}
            localCustody={mergedCustody}
            onProcessReturn={handleProcessReturn}
            onProcessReturnAssignment={handleProcessReturnAssignment}
          />
        )}
        {tab === 'history' && (
          <HistoryTab localLedger={localLedger} params={params} setParams={setParams} />
        )}
      </PageWrapper>
    </>
  )
}
